import React, { useState } from 'react';
import { apiFetch, parseApiResponse } from '../api';
import { toast } from '../utils/toast';

export function getBugReportSubmitErrorMessage(error) {
  if (error?.code === 'schema_not_ready') {
    return 'Bug reporting is temporarily unavailable while we finish a database update. Please try again soon.';
  }
  return error?.message || 'Failed to submit bug report';
}

export default function BugReportButton({ isChatPage = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('medium');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Please describe the issue.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/api/bugs/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'manual',
          title: title.trim() || 'User Reported Bug',
          description: description.trim(),
          severity,
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
      });
      const result = await parseApiResponse(response, 'bugs.report.manual');
      if (!result.ok) {
        throw new Error(getBugReportSubmitErrorMessage(result.error));
      }
      toast.success('Bug report submitted. Thank you!');
      reset();
      setIsOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`lrbrain-bug-fab${isChatPage ? ' chat-offset' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Report bug"
      >
        Report Bug
      </button>

      <style>{`
        .lrbrain-bug-fab {
          position: fixed;
          right: 1.25rem;
          bottom: 1.25rem;
          z-index: 35;
          border: 1px solid var(--lrbrain-border);
          border-radius: 9999px;
          background: var(--lrbrain-white);
          color: var(--lrbrain-text);
          font-family: inherit;
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 0.55rem 0.9rem;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .lrbrain-bug-fab:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 22px rgba(0, 0, 0, 0.2);
          background: #f8fbff;
        }

        .lrbrain-bug-fab.chat-offset {
          bottom: 56px;
        }

        @media (max-width: 768px) {
          .lrbrain-bug-fab {
            right: 0.9rem;
            bottom: 0.9rem;
          }

          .lrbrain-bug-fab.chat-offset {
            bottom: 56px;
          }
        }
      `}</style>

      {isOpen && (
        <div className="lrbrain-auth-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="lrbrain-auth-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="lrbrain-auth-modal-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              type="button"
            >
              x
            </button>
            <div className="lrbrain-auth-modal-header">
              <p className="eyebrow">Bug Report</p>
              <h2>Report an issue</h2>
              <p>Tell us what went wrong so we can fix it quickly.</p>
            </div>
            <form className="lrbrain-auth-form" onSubmit={onSubmit}>
              <label className="form-field">
                <span>Title (optional)</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short summary"
                />
              </label>

              <label className="form-field">
                <span>Severity</span>
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </label>

              <label className="form-field">
                <span>Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  placeholder="What happened, what you expected, and steps to reproduce."
                  required
                />
              </label>

              <button type="submit" className="lrbrain-btn lrbrain-btn-solid" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
